import { useState, useEffect, useCallback, useMemo } from 'react';
import { adminApi } from '../../../services/api';

// Shared client-selection logic used by both "Regala Punti" and "Regala
// Coupon" tabs. Keeps the criteria 100% identical and lets both tabs read
// the same `payload` shape to send to their respective endpoints.
export function useGiftTarget() {
  const [mode, setMode] = useState('segment'); // "segment" | "single"
  const [selectedTiers, setSelectedTiers] = useState([]);
  const [segment, setSegment] = useState('');
  const [inactiveDays, setInactiveDays] = useState('30');
  const [newDays, setNewDays] = useState('7');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Autocomplete suggestions (single mode only)
  useEffect(() => {
    if (mode !== 'single' || searchQuery.length < 2) {
      setSuggestions([]); setShowSuggestions(false); return;
    }
    const t = setTimeout(async () => {
      try {
        const res = await adminApi.get(`/customers/suggest?q=${encodeURIComponent(searchQuery)}`);
        const list = Array.isArray(res.data) ? res.data : (res.data?.customers || []);
        setSuggestions(list);
        setShowSuggestions(list.length > 0);
      } catch { setSuggestions([]); setShowSuggestions(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [searchQuery, mode]);

  const selectCustomer = useCallback((c) => {
    setSearchResult(c);
    setSearchQuery(c.name);
    setShowSuggestions(false);
    setSuggestions([]);
  }, []);

  const clearSelection = useCallback(() => {
    setSearchResult(null); setSearchQuery('');
  }, []);

  const toggleTier = useCallback((tier) => {
    setSelectedTiers(prev => prev.includes(tier) ? prev.filter(t => t !== tier) : [...prev, tier]);
  }, []);

  // The payload fields shared by /admin/gift-points and /admin/gift-coupon
  // (and their /preview variants). Tab-specific fields (points, coupon_id,
  // note, channels) are merged in by the caller.
  const targetPayload = useMemo(() => {
    if (mode === 'single') {
      return searchResult ? { customer_id: searchResult.id } : null;
    }
    const body = {};
    if (selectedTiers.length) body.tier_filter = selectedTiers;
    if (segment) {
      body.segment = segment;
      if (segment === 'inactive') body.inactive_days = parseInt(inactiveDays) || 30;
      if (segment === 'new') body.new_days = parseInt(newDays) || 7;
    }
    return body;
  }, [mode, searchResult, selectedTiers, segment, inactiveDays, newDays]);

  const isValid = mode === 'single' ? !!searchResult : true;

  return {
    state: { mode, selectedTiers, segment, inactiveDays, newDays,
             searchQuery, searchResult, suggestions, showSuggestions },
    setters: { setMode, setSegment, setInactiveDays, setNewDays,
               setSearchQuery, setShowSuggestions },
    actions: { selectCustomer, clearSelection, toggleTier },
    targetPayload,
    isValid,
  };
}
